import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import EmptyState from '../components/EmptyState';

// Lista as chaves PIX do cliente logado. Backend retorna Pendentes + Pagas
// dos ultimos 7 dias em uma unica chamada; o toggle abaixo apenas filtra
// no cliente qual grupo mostrar. A chave copia-e-cola (brcode) so aparece
// na tela de detalhe (/minhas-chaves-pix/:id).

const FILTROS = [
  { id: 'pendente', label: 'Pendentes', status: 'Pendente' },
  { id: 'pagas',    label: 'Pagas (7 dias)', status: 'Pagamento efetuado!' },
];

function formatarMoeda(valor) {
  if (valor == null) return null;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
    .format(parseFloat(valor));
}

function formatarData(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export default function MinhasChavesPix() {
  const [chaves, setChaves] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtro, setFiltro] = useState('pendente');
  const navigate = useNavigate();

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get('/pix/minhas');
      setChaves(data);
      setErro(null);
    } catch (e) {
      setErro(e?.response?.data?.error || 'Nao foi possivel carregar suas chaves PIX.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const chavesFiltradas = useMemo(() => {
    const alvo = FILTROS.find(f => f.id === filtro)?.status;
    return chaves.filter(c => c.status === alvo);
  }, [chaves, filtro]);

  const contagem = useMemo(() => {
    const c = { pendente: 0, pagas: 0 };
    for (const x of chaves) {
      if (x.status === 'Pendente') c.pendente += 1;
      else if (x.status === 'Pagamento efetuado!') c.pagas += 1;
    }
    return c;
  }, [chaves]);

  return (
    <>
      <div className="hero-cli">
        <h1><i className="bi bi-qr-code" style={{ marginRight: 10 }}></i>Minhas chaves PIX</h1>
        <p>Chaves PIX vinculadas ao seu CPF/CNPJ. Pagas dos ultimos 7 dias ficam disponiveis para consulta.</p>
      </div>

      <div className="card-cli" style={{ padding: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {FILTROS.map(f => {
            const ativo = filtro === f.id;
            const total = f.id === 'pendente' ? contagem.pendente : contagem.pagas;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFiltro(f.id)}
                className={ativo ? 'btn-verde-cli' : 'btn-ghost-cli'}
                style={{ padding: '8px 14px' }}
              >
                {f.label}
                <span className="tag-cli" style={{ marginLeft: 8, background: ativo ? 'rgba(255,255,255,.25)' : undefined, color: ativo ? '#fff' : undefined }}>
                  {total}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {carregando ? (
        <div className="card-cli">
          <p style={{ color: 'var(--texto-suave)', margin: 0 }}>Carregando…</p>
        </div>
      ) : erro ? (
        <div className="card-cli" style={{ borderColor: '#f4baba', background: '#fde2e2' }}>
          <p style={{ color: '#9b2424', margin: 0 }}>{erro}</p>
        </div>
      ) : chavesFiltradas.length === 0 ? (
        <div className="card-cli">
          <EmptyState
            icon={filtro === 'pendente' ? 'bi-check2-circle' : 'bi-hourglass'}
            titulo={filtro === 'pendente' ? 'Nenhuma chave PIX pendente' : 'Nenhum pagamento nos ultimos 7 dias'}
            mensagem={filtro === 'pendente'
              ? 'Quando houver uma chave PIX aguardando pagamento, ela aparecera aqui.'
              : 'Pagamentos confirmados nos ultimos 7 dias ficam listados aqui.'}
          />
        </div>
      ) : (
        <div className="card-cli" style={{ padding: 0 }}>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {chavesFiltradas.map((c, i) => {
              const pendente = c.status === 'Pendente';
              return (
                <li
                  key={c.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '14px 16px',
                    borderTop: i === 0 ? 'none' : '1px solid var(--cinza-borda)',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span className={'tag-cli ' + (pendente ? 'laranja' : '')}>
                        <i className={'bi ' + (pendente ? 'bi-hourglass-split' : 'bi-check-circle-fill')} style={{ marginRight: 4 }} />
                        {pendente ? 'Pendente' : 'Pago'}
                      </span>
                      <strong style={{ color: 'var(--verde-escuro)', fontSize: '1.05rem' }}>
                        {formatarMoeda(c.valor)}
                      </strong>
                    </div>
                    <div style={{ fontSize: '.82rem', color: 'var(--texto-suave)' }}>
                      Pedido <strong>{c.numped}</strong> · {formatarData(c.dataGeracao)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={pendente ? 'btn-laranja-cli' : 'btn-ghost-cli'}
                    onClick={() => navigate(`/minhas-chaves-pix/${c.id}`)}
                    style={{ padding: '8px 14px' }}
                  >
                    <i className="bi bi-arrow-right-circle" style={{ marginRight: 6 }} />
                    Ver detalhes
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
}
